import { useState, useEffect, useRef } from "react";
import { useInView } from "framer-motion";

/**
 * Animated count-up number component.
 * Counts from 0 to `target` over `duration` ms when visible in viewport.
 */
export function AnimatedCounter({ target, duration = 2000, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current || target <= 0) return;
    hasAnimated.current = true;

    const startTime = performance.now();
    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(step);
  }, [isInView, target, duration]);

  return (
    <span ref={ref} data-testid="animated-counter">
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
}

/**
 * Format large numbers: 1000 -> 1K, 1000000 -> 1M, etc.
 */
export function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toLocaleString();
}

/**
 * Format number with full comma separators: 415000 -> 415,000
 */
export function formatNumberFull(num) {
  return num.toLocaleString();
}
